import { useEffect, useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { useGetBrandCampaignByIdHttp } from "../../hooks/http/campaigns/get-brand-campaign-by-id.hook";
import { formatDate, getBarColor, handleHttpError } from "../../../utils/utils";
import { Outlet, useParams } from "react-router-dom";
import {
	CampaignStatus,
	type IBrandCampaign,
} from "../../hooks/http/campaigns/get-brand-campaigns.hook";
import Spinner from "../../../components/spinner.components";
import AddImageIcon from "../../../assets/svgs/common/add-image.svg";
import { Badge } from "../../components/core/badge.core";
import TickIcon from "../../../assets/svgs/common/tick.svg";
import ClockIcon from "../../../assets/svgs/common/clock.svg";
import millify from "millify";
import StatsHomeDashboardBrandPartial from "../../../partials/brand/home/stats-home-dashboard.brand.partials";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import { type ICampaignBrandMetricMinimal, useGetCampaignMetricsHttp } from "../../hooks/http/campaigns/get-campaign-metrics.hook";
import { Linechart } from "../../components/ui/line.chart.ui";
import { Barchart } from "../../components/ui/barchart.ui";

export default function CampaignMetricsPartial() {
	const { campaignId } = useParams();

	const [campaignData, setCampaignData] = useState<IBrandCampaign>();
	const [campaignMetrics, setCampaignMetrics] =
		useState<ICampaignBrandMetricMinimal>();

	const { toast } = useToast();

	const { execute: getBrandCampaignByIdHttp, fetching: gettingBrandCampaign } =
		useGetBrandCampaignByIdHttp();

	const { execute: getCampaignMetricsHttp, fetching: gettingCampaignMetrics } =
		useGetCampaignMetricsHttp();

	async function getBrandCampaignById(campaignId: string) {
		const response = await getBrandCampaignByIdHttp({
			body: null,
			pathParams: {
				campaignId: campaignId,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setCampaignData(response.data.campaign);
	}

	async function getCampaignMetrics() {
		if (!campaignId) return;
		const response = await getCampaignMetricsHttp({
			body: null,
			pathParams: {
				id: campaignId,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setCampaignMetrics(response.data.metrics);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (campaignId) {
			getBrandCampaignById(campaignId);
			getCampaignMetrics();
		}
	}, []);

	const PostPerDayChartData = [
		{
			category: "Total Posts",
			value: millify(campaignMetrics?.totalPosts ?? 0),
		},
	];

	const TotalViewsChartData = [
		{
			category: "Total views",
			value: millify(campaignMetrics?.totalViews ?? 0),
		},
	];

	const ApprovedPostsChartData = [
		{
			category: "Total Posts",
			value: campaignMetrics?.totalPosts ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Approved Posts",
			value: campaignMetrics?.approvedPosts ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Rejected Posts",
			value: campaignMetrics?.rejectedPosts ?? 0,
			fill: getBarColor(2),
		},
	];

	const AverageViewsPerPostChartData = [
		{
			category: "Views",
			value: campaignMetrics?.averageViewsPerPost ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Likes",
			value: campaignMetrics?.averageLikesPerPost ?? 0,
			fill: getBarColor(2),
		},
		{
			category: "Comments",
			value: campaignMetrics?.averageCommentsPerPost ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Shares",
			value: campaignMetrics?.averageSharesPerPost ?? 0,
			fill: getBarColor(3),
		},
	];

	if (gettingBrandCampaign || gettingCampaignMetrics) {
		return (
			<div className="flex justify-center items-center w-full h-full">
				<Spinner width={20} />
			</div>
		);
	}
	return (
		<div className="px-10 pt-10 w-full">
			{gettingBrandCampaign ? (
				<div className="flex justify-center items-center w-full h-96">
					<Spinner />
				</div>
			) : (
				<div className="md:flex gap-6 w-full">
					{campaignData?.coverImage?.url ? (
						<img
							className="border rounded-lg w-full max-w-md h-60 object-cover"
							src={`${campaignData.coverImage.url}`}
							alt=""
						/>
					) : (
						<div className="flex justify-center items-center border rounded-lg w-full max-w-md h-60">
							<AddImageIcon className="fill-black" />
						</div>
					)}

					<div className="flex justify-between items-start w-full">
						<div className="flex flex-col justify-between w-full h-full">
							<div className="flex justify-between items-start w-full">
								<div className="flex flex-col gap-6">
									<div className="flex items-center gap-4">
										{campaignData?.campaignStatus.status ===
											CampaignStatus.live && (
											<Badge
												variant="3d"
												className="flex justify-center items-center gap-1 bg-primarybuttonbg p-2 w-16"
											>
												<TickIcon />
												live
											</Badge>
										)}

										<div className="flex items-center gap-4">
											<div className="flex items-center gap-1 px-4 py-2 border rounded-full">
												<ClockIcon />
												<p
													className="font-semibold text-base"
													// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
													dangerouslySetInnerHTML={{
														__html:
															campaignData?.startDate && campaignData?.endDate
																? `${formatDate(campaignData.startDate)} - ${formatDate(campaignData.endDate)}`
																: "",
													}}
												/>
											</div>
										</div>
									</div>

									<h1 className="font-semibold text-3xl text-black">
										{campaignData?.name}
									</h1>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			<div className="flex flex-col gap-10 py-10">
				<StatsHomeDashboardBrandPartial
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
				/>
				<Barchart
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					chartData={ApprovedPostsChartData}
					title="Approved posts"
				/>

				<Linechart
					lineData={campaignMetrics?.postsPerDay || []}
					title="Posts per day"
					dataKey="posts"
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					chartData={PostPerDayChartData}
				/>
				<Linechart
					lineData={campaignMetrics?.totalViewsChart || []}
					title="Total views"
					dataKey="views"
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					chartData={TotalViewsChartData}
				/>
				<Barchart
					campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
					chartData={AverageViewsPerPostChartData}
					title="Average views per post"
				/>
			</div>

			<Outlet />
		</div>
	);
}
